import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';

function statusColor(s) {
  if (s === 'active' || s === 'trialing') return 'success';
  if (s === 'past_due') return 'warning';
  return 'default';
}

export default function TenantSubscriptionPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch('/api/tenant/subscription');
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return <Alert severity="error">{error}</Alert>;
  }

  const sub = data?.subscription;
  const limits = sub?.plan_limits;
  const policy = data?.usagePolicy;
  const summary = data?.usageSummary;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  const kpis = summary?.kpis;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Subscription
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Current live subscription (trialing, active, or past due). Contact your account team for billing changes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!sub && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography color="text.secondary">
            No active subscription record. Your organization may still be on a plan assigned directly — check Plans
            or contact support.
          </Typography>
        </Paper>
      )}

      {sub && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6">{sub.plan_name}</Typography>
            <Chip size="small" label={sub.subscription_status} color={statusColor(sub.subscription_status)} />
          </Box>
          <Grid container spacing={2}>
            {row('Plan code', sub.plan_code)}
            {row('Billing cycle', sub.billing_cycle)}
            {row('Price snapshot', sub.price_snapshot != null ? sub.price_snapshot.toFixed(2) : '—')}
            {row('Start', sub.start_date)}
            {row('End', sub.end_date)}
            {row('Renewal', sub.renewal_date)}
            {row('Canceled', sub.cancellation_date)}
          </Grid>
          {limits && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Plan allowances (catalog)
              </Typography>
              <Grid container spacing={2}>
                {row('API call limit (plan field)', limits.api_call_limit)}
                {row('Max users', limits.max_users)}
                {row('Max API keys', limits.max_api_keys)}
                {row('Storage (MB)', limits.storage_limit_mb)}
              </Grid>
            </>
          )}
        </Paper>
      )}

      {policy?.effective && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Usage policy (effective)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Resolution: {policy.resolution} · Enforcement: {policy.effective?.enforcement_mode || '—'}
          </Typography>
          <Grid container spacing={2}>
            {row('Requests / minute', policy.effective.request_limit_per_minute ?? 'Unlimited')}
            {row('Daily requests', policy.effective.daily_request_limit ?? 'Unlimited')}
            {row('Monthly requests', policy.effective.monthly_request_limit ?? 'Unlimited')}
          </Grid>
        </Paper>
      )}

      {kpis && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Usage summary (last ~30 days)
          </Typography>
          <Grid container spacing={2}>
            {row('Tenant API requests (range)', kpis.tenantApiRequestsInRange)}
            {row('Active users', kpis.activeUsers)}
            {row('Logins (range)', kpis.loginsInRange)}
          </Grid>
          {summary?.range && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Range: {new Date(summary.range.from).toLocaleDateString()} –{' '}
              {new Date(summary.range.to).toLocaleDateString()}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
